/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Box, Button, Dialog, DialogActions, DialogContent, Paper, Typography } from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
// import { Editor } from 'react-draft-wysiwyg';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
// import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import { convertToDraftWysiwygContent } from '../../helpers/commonHelper';
import { Editor as TinyEditor } from '@tinymce/tinymce-react';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';

// ─── Colour tokens (matches GeneralMaterialDailogForm / ConfigureTaskDialog) ──
const BLUE = '#1565c0';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialogRoot: {
            '& .downshift-wrapper': {
                flex: 1,
                padding: 0,
            },
        },

        // ── Header bar (same treatment as GeneralMaterialDailogForm's dialogHeader) ──
        dialogHeader: {
            background: BLUE,
            color: '#fff',
            padding: theme.spacing(1.75, 2.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1.5),
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
        },
        dialogHeaderContent: {
            minWidth: 0,
        },
        dialogHeaderEyebrow: {
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
            color: '#fff',
            opacity: 0.9,
            marginBottom: 2,
        },
        dialogHeaderTitle: {
            fontWeight: 600,
            fontSize: 17,
            color: '#fff',
            lineHeight: 1.2,
        },
        dialogHeaderBadge: {
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 999,
            padding: theme.spacing(0.4, 1.25),
            fontSize: 11,
            fontWeight: 500,
            color: '#fff',
            background: 'rgba(255,255,255,0.1)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },

        // Mirrors FormSectionCard's look — built locally so the card can flex to fill the
        // dialog and host the loading overlay (FormSectionCard's plain children div can't).
        sectionCard: {
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            flex: 1,
            minHeight: 0,
            position: 'relative',
        },
        sectionHead: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            flexShrink: 0,
        },
        sectionIcon: {
            width: 32,
            height: 32,
            borderRadius: 6,
            backgroundColor: '#e3f2fd',
            color: BLUE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        sectionTitle: {
            fontSize: 12,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: BLUE,
            marginBottom: 3,
        },
        sectionDesc: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
        },
        editorWrapper: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            '& .tox-tinymce': {
                flex: 1,
                borderRadius: 8,
                border: `1px solid ${theme.palette.divider}`,
            },
        },

        // ── Footer (same as GeneralMaterialDailogForm's footer) ──
        footer: {
            borderTop: '1px solid #e0e0e0',
            padding: theme.spacing(1.75, 2.5),
            background: '#fff',
        },
        hidden: {
            display: 'none',
        },
        // Covers only the card (not the header) while TinyMCE initialises underneath it
        loaderWrapper: {
            padding: 16,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 'inherit',
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
        },
        formLabel: {
            paddingBottom: '10px',
        },
        editorClassName: {
            padding: '0 10px',
            minHeight: '300px',
            maxHeight: '500px',
        },
        wrapperClassName: {
            border: '1px solid #949494',
            display: 'flex',
            flexFlow: 'column-reverse',
        },
        toolbarClassName: {
            border: 0,
            // borderBottom: '1px solid #F1F1F1',
            margin: 0,
            padding: 0,
            '& .rdw-dropdown-wrapper': {
                position: 'relative',
            },
            '& .rdw-dropdown-optionwrapper': {
                position: 'absolute',
            },
        },
        paperRoot: {
            overflow: 'auto',
            position: 'relative',
            minHeight: 'calc(100vh - 50px)',
            display: 'flex',
            flexFlow: 'column',
        },
        dialogContent: {
            overflow: 'inherit',
            // minHeight: '538px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            padding: theme.spacing(2.5),
            backgroundColor: '#f5f5f5',
        },
    }),
);

interface FeatureNotesPreviewDialogProps {
    open: boolean;
    onClose?: () => void;
    description: string;
}

export interface FeatureNotesPreviewDialogForm {
    notes: string; // EditorState;
}

export const FeatureNotesPreviewDialog: React.FC<FeatureNotesPreviewDialogProps> = (props) => {
    const classes = useStyles();

    const [isLoading, setIsLoading] = React.useState(true);

    let tempDesription = props.description;

    if (props.description) {    
        if (props.description.indexOf('blocks":') !== -1) {
            const draftContent = convertToDraftWysiwygContent(JSON.parse(props.description));
            const contentState = convertFromRaw(draftContent);
            tempDesription = draftToHtml(convertToRaw(EditorState.createWithContent(contentState).getCurrentContent()));
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            maxWidth="md"
            fullWidth={true}
            className={classes.dialogRoot}
            scroll={'paper'}
            // disablePortal={false}
            disableScrollLock={true}
            disableEnforceFocus={true}
            disableAutoFocus={true}
            classes={{
                paper: classes.paperRoot,
            }}
        >
            <Box className={classes.dialogHeader}>
                <Box className={classes.dialogHeaderContent}>
                    <Typography className={classes.dialogHeaderEyebrow}>Preview</Typography>
                    <Typography className={classes.dialogHeaderTitle}>Feature Notes</Typography>
                </Box>
                <Typography className={classes.dialogHeaderBadge}>Read only</Typography>
            </Box>
            <DialogContent dividers={true} className={classes.dialogContent}>
                <Paper className={classes.sectionCard}>
                    {isLoading ? (
                        <div className={classes.loaderWrapper}>
                            <SkeletonLoader variant="text" rows={8} />
                        </div>
                    ) : null}
                    <div className={classes.sectionHead}>
                        <div className={classes.sectionIcon}>
                            <VisibilityOutlinedIcon fontSize="small" />
                        </div>
                        <div>
                            <Typography className={classes.sectionTitle}>Notes</Typography>
                            <Typography className={classes.sectionDesc}>
                                What users see when they open this feature's information.
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.editorWrapper}>
                        <TinyEditor
                            apiKey={'k74nddluc9fmtf75en31ew8mxzqcvovgpjkzomdtgeje7b0h'}
                            initialValue={tempDesription}
                            disabled={true}
                            init={{
                                height: '100%',
                                autoresize_min_height: '100%',
                                autoresize_max_height: '100%',
                                menubar: false,
                                disabled: true, // Set the editor in read-only mode
                                plugins: '', // No plugins needed for read-only mode
                                toolbar: '', // No toolbar needed for read-only mode
                                statusbar: false, // Hide the status bar
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                toolbar_sticky: true,
                                autosave_ask_before_unload: true,
                                autosave_interval: '30s',
                                autosave_prefix: '{path}{query}-{id}-',
                                autosave_restore_when_empty: false,
                                autosave_retention: '2m',
                                image_advtab: true,
                                content_css: '//www.tiny.cloud/css/codepen.min.css',
                                image_class_list: [
                                    { title: 'None', value: '' },
                                    { title: 'Some class', value: 'class-name' },
                                ],
                                importcss_append: true,
                                min_height: 500,
                                // min_width: 300,
                            }}
                            onInit={(evt, editor) => {
                                setIsLoading(false);
                            }}
                        />
                    </div>
                </Paper>
            </DialogContent>
            <DialogActions className={classes.footer}>
                <Button color="primary" variant="contained" type="button" onClick={props.onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};
